import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import {
  buildResolvedRouteState,
  createAnalyticsRuntime,
} from '../../analytics/ga4Runtime';

const AnalyticsTracker = () => {
  const location = useLocation();
  const runtimeRef = useRef(null);
  const scrollRafRef = useRef(0);

  if (!runtimeRef.current && typeof window !== 'undefined' && typeof document !== 'undefined') {
    runtimeRef.current = createAnalyticsRuntime({ windowObj: window, documentObj: document });
  }

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
    if (!runtimeRef.current) return undefined;

    const runtime = runtimeRef.current;
    let pendingRaf = 0;

    const scheduleScrollCheck = () => {
      if (scrollRafRef.current) return;
      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = 0;
        runtime.checkScrollDepth();
      });
    };

    const onClick = (event) => runtime.handleDocumentClick(event);
    const onSubmit = (event) => runtime.handleFormSubmit(event);
    const onScroll = () => scheduleScrollCheck();
    const onResize = () => scheduleScrollCheck();

    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onSubmit, true);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    pendingRaf = window.requestAnimationFrame(() => {
      runtime.checkScrollDepth();
    });

    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('submit', onSubmit, true);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (pendingRaf) window.cancelAnimationFrame(pendingRaf);
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = 0;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
    if (!runtimeRef.current) return undefined;

    const runtime = runtimeRef.current;
    const routeState = buildResolvedRouteState(location.pathname, location.search, window.location.hostname);
    runtime.setCurrentRouteState(routeState);

    let raf1 = 0;
    let raf2 = 0;

    // Run after route metadata effects have updated document.title/canonical.
    raf1 = window.requestAnimationFrame(() => {
      routeState.title = document.title || routeState.meta?.title || routeState.title || '';
      runtime.trackCurrentPageView();
      raf2 = window.requestAnimationFrame(() => {
        runtime.checkScrollDepth();
      });
    });

    return () => {
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [location.pathname, location.search]);

  return null;
};

export default AnalyticsTracker;
